import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectProps,
  FormControlProps,
  InputLabelProps,
  MenuItemProps,
} from '@mui/material';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface CustomSelectProps extends Omit<SelectProps, 'onChange'> {
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
  formControlProps?: Partial<FormControlProps>;
  inputLabelProps?: Partial<InputLabelProps>;
  menuItemProps?: Partial<MenuItemProps>;
  id?: string;
  'data-test-id'?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  options,
  value,
  onChange,
  required = false,
  error = false,
  helperText,
  fullWidth = true,
  disabled = false,
  placeholder = 'Select an option',
  formControlProps,
  inputLabelProps,
  menuItemProps,
  id,
  'data-test-id': dataTestId,
  ...selectProps
}) => {
  const handleChange = (event: any) => {
    onChange(event.target.value);
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      required={required}
      error={error}
      disabled={disabled}
      {...formControlProps}
    >
      <InputLabel
        id={`${label}-label`}
        data-test-id={`${dataTestId}-label`}
        sx={{ fontFamily: 'var(--font-noto-sans)' }}
        {...inputLabelProps}
      >
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        value={value}
        onChange={handleChange}
        label={label}
        id={id}
        data-test-id={dataTestId}
        sx={{ fontFamily: 'var(--font-noto-sans)' }}
        {...selectProps}
      >
        <MenuItem
          value=""
          sx={{ fontFamily: 'var(--font-noto-sans)' }}
          id={`${label}-empty`}
          data-test-id={`${label}-empty`}
          {...menuItemProps}
        >
          {placeholder}
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            sx={{ fontFamily: 'var(--font-noto-sans)' }}
            id={`${label}-${option.value}`}
            data-test-id={`${label}-${option.value}`}
            {...menuItemProps}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CustomSelect; 